import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { postApproveKyc, useAdminInspectorsCache } from '../../../api/admin-inspectors';
import { publishToast } from '../../../components/feedback';

/**
 * Encapsulates the KYC manual-approve orchestration for the inspector
 * detail drawer (AUT-125): fires the destructive POST, a success / error
 * toast, **patches the detail cache from the response** (architect 🟠 #5 —
 * the api returns the freshly-projected `AdminInspectorDetailDto`;
 * cross-patching avoids the 200ms window where the drawer still shows the
 * "Одобрить KYC" footer while the refetch is in flight, racing into another
 * 409), then invalidates the `['admin','inspectors']` prefix so the list
 * refetches too. The action is role + permission + audit gated server-side
 * (AUT-138 removed per-action TOTP step-up).
 */
export interface UseApproveKycResult {
  /** True while the mutation is in flight. */
  busy: boolean;
  /** Run an approve; resolves true on success, false on error. */
  run: (inspectorId: string) => Promise<boolean>;
}

export const useApproveKyc = (): UseApproveKycResult => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const { patchDetail, invalidate } = useAdminInspectorsCache();

  const run = async (inspectorId: string): Promise<boolean> => {
    setBusy(true);
    try {
      const fresh = await postApproveKyc(inspectorId);
      // Patch FIRST (architect 🟠 #5) — the drawer reads detail from the
      // cache; this is what keeps the "Одобрить KYC" button from
      // flickering back on for the refetch window.
      patchDetail(inspectorId, fresh);
      await invalidate();
      publishToast({
        severity: 'success',
        durationMs: 4000,
        message: t('inspectors.approveKycDialog.successToast'),
      });
      return true;
    } catch {
      publishToast({
        severity: 'error',
        durationMs: 6000,
        message: t('inspectors.approveKycDialog.errorToast'),
      });
      return false;
    } finally {
      setBusy(false);
    }
  };

  return { busy, run };
};
